import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { CharacterDto, CharacterTypeDto, MeDto } from '@bb/shared';
import { api, ApiError } from '../net/api';
import { AvatarPreview } from './AvatarPreview';
import { Field } from './Field';

const OPTION_LABELS: Record<string, string> = {
  furColor: 'Fur colour',
  skinTone: 'Skin tone',
  hairStyle: 'Hair',
  hairColor: 'Hair colour',
  eyes: 'Eyes',
};

const isColor = (v: string) => v.startsWith('#');

function defaults(type: CharacterTypeDto): Record<string, string> {
  return Object.fromEntries(Object.entries(type.appearanceOptions).map(([k, vals]) => [k, vals[0] ?? '']));
}

export function Creator({ me, onCreated, onSignOut, onCancel }: { me: MeDto; onCreated: (c: CharacterDto) => void; onSignOut: () => void; onCancel?: () => void }) {
  const [types, setTypes] = useState<CharacterTypeDto[]>([]);
  const [typeKey, setTypeKey] = useState<string>();
  const [species, setSpecies] = useState<string>();
  const [appearance, setAppearance] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [error, setError] = useState<string>();
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.characterTypes().then(({ types }) => {
      setTypes(types);
      const first = types[0];
      if (first) choose(first);
    }, (e: Error) => setError(e.message));
  }, []);

  const type = types.find((t) => t.key === typeKey);
  function choose(t: CharacterTypeDto) {
    setTypeKey(t.key);
    setSpecies(t.species[0]?.key);
    setAppearance(defaults(t));
  }

  const look = useMemo(
    () => ({ characterType: typeKey ?? 'creature', species: species ?? null, appearance }),
    [typeKey, species, appearance],
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!type) return;
    setBusy(true);
    setError(undefined);
    setFields({});
    try {
      const { character } = await api.createCharacter({ characterType: type.key, species, displayName: name, appearance });
      onCreated(character);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="creator">
      <header className="topbar">
        <span className="brand small">Buddy Bay</span>
        <span className="muted">Signed in as {me.user.username}</span>
        {onCancel ? <button className="btn ghost" onClick={onCancel}>Back to the game</button> : <button className="btn ghost" onClick={onSignOut}>Sign out</button>}
      </header>
      <main className="creator-body">
        <section className="panel preview-panel">
          <h2>Make your buddy</h2>
          <div className="preview-stage"><AvatarPreview look={look} /></div>
          <p className="muted small-print">{type?.name}{species && type ? ` · ${type.species.find((s) => s.key === species)?.name}` : ''}</p>
        </section>
        <form className="panel options-panel" onSubmit={submit} noValidate>
          <fieldset>
            <legend>What are you?</legend>
            <div className="choice-row">
              {types.map((t) => (
                <button type="button" key={t.key} className={t.key === typeKey ? 'choice on' : 'choice'} aria-pressed={t.key === typeKey} onClick={() => choose(t)}>
                  <AvatarPreview look={{ characterType: t.key, species: t.species[0]?.key ?? null, appearance: defaults(t) }} scale={0.9} />
                  <span>{t.name}</span>
                </button>
              ))}
            </div>
          </fieldset>

          {type && type.species.length > 0 && (
            <fieldset>
              <legend>Species</legend>
              <div className="chip-row">
                {type.species.map((s) => (
                  <button type="button" key={s.key} className={s.key === species ? 'chip on' : 'chip'} aria-pressed={s.key === species} onClick={() => setSpecies(s.key)}>
                    {s.name}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {type &&
            Object.entries(type.appearanceOptions).map(([key, values]) => (
              <fieldset key={key}>
                <legend>{OPTION_LABELS[key] ?? key}</legend>
                <div className="chip-row">
                  {values.map((v) =>
                    isColor(v) ? (
                      <button type="button" key={v} className={appearance[key] === v ? 'swatch on' : 'swatch'} style={{ background: v }} aria-label={`${OPTION_LABELS[key] ?? key} ${v}`} aria-pressed={appearance[key] === v} onClick={() => setAppearance({ ...appearance, [key]: v })} />
                    ) : (
                      <button type="button" key={v} className={appearance[key] === v ? 'chip on' : 'chip'} aria-pressed={appearance[key] === v} onClick={() => setAppearance({ ...appearance, [key]: v })}>
                        {v}
                      </button>
                    ),
                  )}
                </div>
              </fieldset>
            ))}

          <Field id="displayName" label="Name (3–16 letters, numbers or spaces)" value={name} maxLength={16} onChange={(e) => setName(e.target.value)} error={fields.displayName} required />
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="btn wide" disabled={busy || !type || name.trim().length < 3}>{busy ? 'Creating…' : 'Start exploring'}</button>
        </form>
      </main>
    </div>
  );
}
